import { LitElement, html, nothing } from 'lit';

/**
 * @component po-requirement-choice
 * @description PO requirement choice component. Asks whether a PO is required,
 * and optionally captures the PO number or flags for later.
 *
 * @property {Boolean} choiceOnly - When true, only show Y/N cards (no PO input section).
 *   Used on the confirmation page where po-reference-card handles PO entry separately.
 *
 * @fires {CustomEvent} po-choice-changed - User changed PO selection
 *   detail: { custRefRequired: "Y"|"N", custRef: string }
 */
class PoRequirementChoice extends LitElement {
    static properties = {
        choiceOnly: { type: Boolean },
        _poRequired: { type: String, state: true }, // null | 'yes' | 'no'
        _poValue: { type: String, state: true },
        _dontHavePo: { type: Boolean, state: true },
    };

    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.choiceOnly = false;
        this._poRequired = null;
        this._poValue = '';
        this._dontHavePo = false;
    }

    _selectYes() {
        this._poRequired = 'yes';
        this._emitChange();
    }

    _selectNo() {
        this._poRequired = 'no';
        this._poValue = '';
        this._dontHavePo = false;
        this._emitChange();
    }

    _handlePoInput(e) {
        this._poValue = e.target.value;
        this._emitChange();
    }

    _handleDontHavePo(e) {
        this._dontHavePo = e.target.checked;
        if (this._dontHavePo) {
            this._poValue = '';
        }
        this._emitChange();
    }

    _emitChange() {
        this.dispatchEvent(
            new CustomEvent('po-choice-changed', {
                detail: {
                    custRefRequired: this._poRequired === 'yes' ? 'Y' : 'N',
                    custRef: this._poRequired === 'yes' ? this._poValue.trim() : '',
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    _renderPoInputSection() {
        if (this._poRequired !== 'yes') return nothing;

        return html`
            <div class="mt-4 space-y-3">
                <div>
                    <label class="field-label">PO Number</label>
                    <input
                        type="text"
                        .value="${this._poValue}"
                        @input="${this._handlePoInput}"
                        placeholder="e.g. PO123456"
                        class="input input-bordered w-full"
                        ?disabled="${this._dontHavePo}"
                    />
                </div>
                <label class="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        .checked="${this._dontHavePo}"
                        @change="${this._handleDontHavePo}"
                        class="checkbox checkbox-sm checkbox-primary"
                    />
                    <span class="text-sm text-gray-600">I don't have my PO number yet</span>
                </label>
                ${this._dontHavePo
                    ? html`
                          <p class="text-xs text-gray-500">
                              No problem -- we'll send you a reminder before your invoice is
                              generated.
                          </p>
                      `
                    : nothing}
            </div>
        `;
    }

    render() {
        return html`
            <div>
                <h3 class="text-xl font-bold mb-2">Purchase Order Requirement</h3>
                <p class="text-sm text-gray-600 mb-4">
                    Does this booking require a Purchase Order (PO) number for invoicing?
                </p>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div
                        @click="${this._selectYes}"
                        class="card border-2 cursor-pointer transition-all ${this._poRequired ===
                        'yes'
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-base-300 bg-base-100 hover:border-gray-400'}"
                    >
                        <div class="card-body p-5">
                            <div class="flex items-start gap-3">
                                <div
                                    class="w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${this
                                        ._poRequired === 'yes'
                                        ? 'border-primary'
                                        : 'border-gray-300'}"
                                >
                                    ${this._poRequired === 'yes'
                                        ? html`<div class="w-3 h-3 rounded-full bg-primary"></div>`
                                        : nothing}
                                </div>
                                <div>
                                    <h4 class="font-bold text-base">Yes, PO Required</h4>
                                    <p class="text-sm text-gray-600 mt-1">
                                        This booking requires a purchase order number for invoicing
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        @click="${this._selectNo}"
                        class="card border-2 cursor-pointer transition-all ${this._poRequired ===
                        'no'
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-base-300 bg-base-100 hover:border-gray-400'}"
                    >
                        <div class="card-body p-5">
                            <div class="flex items-start gap-3">
                                <div
                                    class="w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${this
                                        ._poRequired === 'no'
                                        ? 'border-primary'
                                        : 'border-gray-300'}"
                                >
                                    ${this._poRequired === 'no'
                                        ? html`<div class="w-3 h-3 rounded-full bg-primary"></div>`
                                        : nothing}
                                </div>
                                <div>
                                    <h4 class="font-bold text-base">No PO Needed</h4>
                                    <p class="text-sm text-gray-600 mt-1">
                                        Proceed without a purchase order reference
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                ${!this.choiceOnly && this._poRequired === 'yes'
                    ? html`
                          <div class="bg-base-200 rounded-lg p-5 mt-4">
                              ${this._renderPoInputSection()}
                          </div>
                      `
                    : nothing}
            </div>
        `;
    }
}

customElements.define('po-requirement-choice', PoRequirementChoice);
